/**
 * @file personStore.js
 * @description Where the People master schema is STORED — declared once.
 *
 * people.js says what a person record should hold. This file says which of
 * those fields the platform now stores, under which path, who may edit them,
 * and who may see them. Five things are generated from it and nothing else:
 *
 *   users-service  the Mongoose shape of `User.person` and of the fields added
 *                  to the career arrays and to organisation memberships
 *   users-service  the validators for PATCH /profile/person/:section (self) and
 *                  PATCH /users/:id/person/:section (staff)   → personStoreValidate.js
 *   users-service  the public-profile projection (only `public` fields leave)
 *   dashboard      every form on /profile and in the admin user drawer
 *   Schemas screen the "Today" column — a master field listed here IS stored,
 *                  so its status is derived, not typed
 *
 * ── Why one declaration and not four files ───────────────────────────
 *
 * Adding a field used to mean the model, the zod contract, the controller
 * and a hand-written form — four edits, and a strict-mode model silently
 * drops whichever one was forgotten. That is exactly how `about.avatarIcon`
 * came to be accepted by the contract, read by three screens, and never
 * stored. Here there is one edit, and a field cannot be half-declared.
 *
 * No imports. The dashboard loads this file in the browser, and the users
 * service loads it at boot; a dependency here would be a dependency of both.
 *
 * Types: string · text · enum · bool · int · number · date · month · list (strings,
 * optionally from `values`) · objects (a list of sub-records, `fields` inside).
 */

import { WORKFORCE_ROLES, RUNGS } from './workforceOptions.js';
// Sibling declarations only, and both are plain value modules the browser can
// load — see the "no imports" note above, which is about dependencies, not
// about re-typing a list of agreement kinds in two places.
import { AGREEMENT_KINDS } from '../legal/agreements.js';

export const PERSON_STORE_VERSION = '2026-09-11';

// ── controlled values ────────────────────────────────────────────────────
export const CEFR = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const CEFR_LABELS = { A1: 'Beginner (A1)', A2: 'Elementary (A2)', B1: 'Intermediate (B1)', B2: 'Upper intermediate (B2)', C1: 'Advanced (C1)', C2: 'Fluent (C2)' };
export const VISIBILITY_LEVELS = ['public', 'members', 'counterparties', 'staff', 'self'];
export const CAPACITY = Object.freeze({
  // The four axes of MOKSY_AI_FACT_BASE.md §3. Asked once, then inferred.
  subject:  ['individual', 'legal_entity'],
  behalf:   ['nobody', 'company', 'education', 'small_business', 'agency', 'government', 'large_corporation', 'startup'],
  intent:   ['build', 'review', 'spend', 'earn', 'invest', 'join_staff', 'partner'],
  standing: ['none', 'founder', 'serial_entrepreneur', 'investor', 'product_owner', 'service_owner', 'skilled_individual']
});

// ── how a choice reads to a person ───────────────────────────────────────
// A form never shows a raw value. `labels` on a field wins; otherwise
// `optionLabel` turns `in_app` into "In app". Codes a person should pick by
// name — countries, time zones, languages, currencies — carry a `picker` and
// are drawn from the browser's own Intl data, so nobody types "GB".
const ROLE_OPTIONS = WORKFORCE_ROLES.map((r) => r.key);
const ROLE_LABELS = Object.fromEntries(WORKFORCE_ROLES.map((r) => [r.key, r.label]));
const AUTONOMY = { guided: 'works with guidance', supported: 'works with support', independent: 'works independently', sets_direction: 'sets direction for others' };
export const RUNG_LABELS = Object.freeze(Object.fromEntries(RUNGS.map((r) => [r.ordinal, `${r.label} — ${AUTONOMY[r.autonomy] || r.autonomy}`])));
const roleKeyField = (extra = {}) => ({ type: 'enum', values: ROLE_OPTIONS, labels: ROLE_LABELS, label: 'Role', ...extra });
const rungField = (extra = {}) => ({ type: 'int', min: 1, max: 8, labels: RUNG_LABELS, label: 'Level', hint: 'How much you lead versus follow in this role.', ...extra });

// The parts of a public profile a person can hide. Listed, not derived, because
// PERSON_SECTIONS below is built with it; a test holds it equal to the sections
// that actually carry a public field.
export const PUBLIC_SECTIONS = Object.freeze(['names', 'languages', 'portfolio', 'availability', 'rates', 'testimonials']);
const PUBLIC_SECTION_LABELS = { names: 'Headline, pronouns and pronunciation', languages: 'Languages', portfolio: 'Portfolio', availability: 'Availability', rates: 'Work I take', testimonials: 'Testimonials' };

export const optionLabel = (field, value) => {
  if (value == null || value === '') return '—';
  const explicit = field?.labels?.[value];
  if (explicit) return explicit;
  const s = String(value).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};


/**
 * The sections of `User.person`. `editor: 'staff'` on a section or a field
 * means the person can read it but only staff can change it.
 */
export const PERSON_SECTIONS = Object.freeze([
  {
    key: 'names', block: 'P02', label: 'Name', audience: 'base', editor: 'self',
    blurb: 'How your name is shown, and how to say it.',
    fields: [
      { key: 'publicNameStyle', type: 'enum', values: ['full', 'first_initial', 'handle'], label: 'Show my name as', master: 'P02.publicNameStyle',
        labels: { full: 'Full name — Ada Lovelace', first_initial: 'First name and initial — Ada L.', handle: 'Username only — @ada' } },
      // P02.headline used to be stored as `about.title` — the SAME field the
      // account form fills from a list of honorifics. So a consultant who
      // picked "Dr" had their public profile headed by the word "Dr", and
      // there was nowhere to write what they actually do (GC-083). A title is
      // how you are addressed; a headline is what you do. Two facts, two
      // fields — the honorific stays on `about.title` and is shown as part of
      // the name, never on a line of its own.
      { key: 'headline', type: 'string', max: 120, label: 'Professional headline', visibility: 'public', master: 'P02.headline',
        placeholder: 'Healthcare data consultant', hint: 'The line under your name on your public profile. Not your title — what you do.' },
      { key: 'pronouns', type: 'string', max: 40, label: 'Pronouns', visibility: 'public', master: 'P02.pronouns', placeholder: 'she/her, they/them…' },
      { key: 'namePronunciation', type: 'string', max: 120, label: 'How to say your name', visibility: 'public', master: 'P02.namePronunciation', placeholder: 'MOK-see' }
    ]
  },
  {
    key: 'contact', block: 'P03', label: 'Contact', audience: 'base', editor: 'self',
    blurb: 'Never shown to anyone you are not already working with.',
    fields: [
      { key: 'phones', type: 'objects', max: 5, label: 'Phone numbers', master: 'P03.phones[]', fields: [
        { key: 'number', type: 'string', max: 20, pattern: '^\\+[1-9][0-9]{6,14}$', label: 'Number', placeholder: '+447700900123', hint: 'International format, starting with +.', required: true },
        { key: 'purpose', type: 'enum', values: ['sms', 'call', 'whatsapp'], label: 'Use for', labels: { sms: 'Text messages', call: 'Calls', whatsapp: 'WhatsApp' } },
        { key: 'primary', type: 'bool', label: 'Primary' }
      ] },
      { key: 'contactChannel', type: 'enum', values: ['email', 'phone', 'in_app', 'linkedin'], label: 'Best way to reach me', master: 'P03.contactChannel',
        labels: { email: 'Email', phone: 'Phone', in_app: 'Messages in Moksy', linkedin: 'LinkedIn' } }
    ]
  },
  {
    key: 'locale', block: 'P04', label: 'Location & locale', audience: 'base', editor: 'self',
    blurb: 'Sets your market, your time zone for deadlines, and the language and currency Moksy AI uses with you.',
    fields: [
      { key: 'country', type: 'string', max: 2, pattern: '^[A-Z]{2}$', picker: 'country', label: 'Country', visibility: 'members', master: 'P04.country' },
      { key: 'region', type: 'string', max: 80, label: 'Region', visibility: 'members', master: 'P04.region, city' },
      { key: 'city', type: 'string', max: 80, label: 'City', visibility: 'members', master: 'P04.region, city' },
      { key: 'timezone', type: 'string', max: 64, pattern: '^[A-Za-z_]+(/[A-Za-z0-9_+-]+){0,2}$', picker: 'timezone', label: 'Time zone', visibility: 'counterparties', master: 'P04.timezone', hint: 'Deadlines and quiet hours use it.' },
      { key: 'locale', type: 'string', max: 16, pattern: '^[a-z]{2,3}(-[A-Z]{2})?$', picker: 'locale', label: 'Language Moksy AI uses with you', master: 'P04.locale' },
      { key: 'currency', type: 'string', max: 3, pattern: '^[A-Z]{3}$', picker: 'currency', label: 'Currency for costs', master: 'P04.currency' },
      { key: 'units', type: 'enum', values: ['metric', 'imperial'], label: 'Units', master: 'P04.units, dateFormat', labels: { metric: 'Metric (km, kg)', imperial: 'Imperial (miles, lb)' } },
      { key: 'dateFormat', type: 'enum', values: ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'], label: 'Date format', master: 'P04.units, dateFormat',
        labels: { 'DD/MM/YYYY': '31/12/2026', 'MM/DD/YYYY': '12/31/2026', 'YYYY-MM-DD': '2026-12-31' } }
    ]
  },
  {
    key: 'languages', block: 'P05', label: 'Languages you work in', audience: 'base', editor: 'self',
    blurb: 'Work in German goes to people who read German. Add each language you can review or build in, and how well.',
    fields: [
      { key: 'items', type: 'objects', max: 12, label: 'Languages', visibility: 'public',
        master: ['P05.languages[].code', 'P05.languages[].cefr', 'P05.languages[].isNative'], fields: [
          { key: 'code', type: 'string', max: 3, pattern: '^[a-z]{2,3}$', picker: 'language', label: 'Language', required: true },
          { key: 'cefr', type: 'enum', values: CEFR, label: 'How well', labels: CEFR_LABELS },
          { key: 'isNative', type: 'bool', label: 'Native' }
        ] }
    ]
  },
  {
    key: 'portfolio', block: 'P10', label: 'Portfolio', audience: 'reviewer', editor: 'self',
    blurb: 'Work you can show. Pin up to six. Link a Moksy project and it counts as delivered evidence.',
    fields: [
      { key: 'items', type: 'objects', max: 30, label: 'Portfolio items', visibility: 'public',
        master: ['P10.portfolio[].title, kind', 'P10.portfolio[].url / uploadRef', 'P10.portfolio[].role, skills[]', 'P10.portfolio[].projectRef', 'P10.portfolio[].pinned'], fields: [
          { key: 'title', type: 'string', max: 140, label: 'Title', required: true },
          { key: 'kind', type: 'enum', values: ['project', 'sample', 'publication', 'repo', 'talk', 'case_study'], label: 'Kind', labels: { repo: 'Code repository', case_study: 'Case study', sample: 'Work sample' } },
          { key: 'url', type: 'string', max: 500, label: 'Link' },
          { key: 'role', type: 'string', max: 120, label: 'Your role' },
          { key: 'skills', type: 'list', max: 12, label: 'Skills shown' },
          { key: 'projectRef', type: 'string', max: 64, label: 'Moksy project (if it was built here)', hint: 'Paste the project link or id — it then counts as delivered evidence.' },
          { key: 'pinned', type: 'bool', label: 'Pin to the top of my profile' }
        ] }
    ]
  },
  {
    key: 'roles', block: 'P12', label: 'Your roles', audience: 'reviewer', editor: 'self',
    blurb: 'What you do professionally, and at what level. It decides which work you are matched to and what that work pays. Roles given to you by Moksy are shown but only staff can change them.',
    // The same block reads differently to somebody who works FOR Moksy.
    blurbFor: {
      staff: 'Your profession and level at Moksy. It decides which desks you can sit at. Roles given to you by your manager are shown but only they can change them.',
      admin: 'Your profession and level at Moksy. It decides which desks you can sit at.'
    },
    fields: [
      { key: 'items', type: 'objects', max: 8, label: 'Roles', visibility: 'members',
        // A role is a listed role OR the person's own description of one.
        itemNeedsOneOf: ['roleKey', 'ownWords'],
        master: ['P12.roles[].roleKey', 'P12.roles[].rung', 'P12.roles[].primary', 'P12.roles[].source, grantedBy, since'], fields: [
          // Not `required` on its own: somebody whose profession is not on
          // the list says so in their own words instead (GC-021). Picking the
          // nearest wrong role — an accessibility lead as "Tester", a Green
          // Book economist as "Accounting" — fed matching and the rate card a
          // speciality the person does not have; a blank "other" would lose
          // what they are. Their words are kept on the role, and captured
          // into the profession vocabulary so the missing role is learned.
          roleKeyField({ key: 'roleKey', hint: 'The closest role on the list. If none fits, leave it and describe yours below.' }),
          { key: 'ownWords', type: 'string', max: 120, label: 'In your own words', vocabulary: 'profession',
            hint: 'What you do, if the list does not have it — e.g. "Heritage conservation adviser".' },
          rungField({ key: 'rung' }),
          { key: 'primary', type: 'bool', label: 'My main role' },
          // `selfLabels`: how the value reads to the person it is about. The
          // staff wording ("Stated by them") is third person, and a role the
          // person stated themselves needs no provenance at all (GC-076).
          { key: 'source', type: 'enum', values: ['self', 'assessed', 'granted'], label: 'Source', editor: 'staff', labels: { self: 'Stated by them', assessed: 'Assessed', granted: 'Granted by Moksy' },
            selfLabels: { assessed: 'Assessed by Moksy', granted: 'Given by Moksy' } },
          { key: 'grantedBy', type: 'string', max: 64, label: 'Granted by', editor: 'staff' },
          { key: 'since', type: 'date', label: 'Since', editor: 'staff' }
        ] }
    ]
  },
  {
    key: 'responsibilities', block: 'P13', label: 'Responsibilities', audience: 'staff', editor: 'staff',
    blurb: 'Reporting line, desk seats, account manager and duty changes. Set by staff.',
    fields: [
      { key: 'reportsTo', type: 'string', max: 64, picker: 'person', label: 'Reports to', master: 'P13.reportsTo' },
      { key: 'accountManagerId', type: 'string', max: 64, picker: 'person', label: 'Account manager', hint: 'A staff member, or moksy_ai for Moksy AI.', master: ['P13.accountManagerId', 'P33.accountManagerId'] },
      { key: 'deskSeats', type: 'objects', max: 12, label: 'Desk seats', master: 'P13.deskSeats[]', fields: [
        { key: 'deskKey', type: 'string', max: 60, label: 'Desk', required: true },
        { key: 'since', type: 'date', label: 'Since' }
      ] },
      { key: 'dutyOverrides', type: 'objects', max: 40, label: 'Duty overrides', master: 'P13.dutyOverrides[]', fields: [
        { key: 'taskKey', type: 'string', max: 80, label: 'Task', required: true },
        { key: 'change', type: 'enum', values: ['add', 'remove'], label: 'Change', required: true, labels: { add: 'Add this duty', remove: 'Remove this duty' } },
        { key: 'reason', type: 'string', max: 280, label: 'Reason' }
      ] }
    ]
  },
  {
    key: 'availability', block: 'P16', label: 'Availability', audience: 'reviewer', editor: 'self',
    blurb: 'Saving confirms it is still true. If you have not confirmed it for 60 days, we treat you as unavailable rather than send you work you cannot take.',
    blurbFor: { staff: 'The hours you work, so desk cover can be planned around them. Saving confirms they are still true.' },
    fields: [
      { key: 'hoursPerWeek', type: 'int', min: 0, max: 50, label: 'Hours per week', visibility: 'counterparties', master: 'P16.hoursPerWeek' },
      { key: 'availableFrom', type: 'date', label: 'Available from', visibility: 'public', master: 'P16.availableFrom' },
      { key: 'lastConfirmedAt', type: 'date', label: 'Last confirmed', stamped: true, master: 'P16.lastConfirmedAt' }
    ]
  },
  {
    key: 'rates', block: 'P17', label: 'Rates & terms', audience: 'reviewer', editor: 'self',
    blurb: 'Your floor above the Moksy rate card, and the work you take.',
    fields: [
      { key: 'rateCard', type: 'objects', max: 12, label: 'Rates', visibility: 'counterparties', master: 'P17.rateCard[]', fields: [
        { key: 'unit', type: 'enum', values: ['hour', 'day', 'fixed', 'review'], label: 'Per', required: true, labels: { hour: 'Hour', day: 'Day', fixed: 'Fixed-price job', review: 'Review' } },
        { key: 'amount', type: 'number', min: 0, max: 100000, label: 'Amount', required: true },
        { key: 'currency', type: 'string', max: 3, pattern: '^[A-Z]{3}$', picker: 'currency', label: 'Currency' },
        { key: 'scope', type: 'string', max: 80, label: 'For (role, category or band)' }
      ] },
      { key: 'engagementTypes', type: 'list', values: ['review', 'build', 'advisory', 'fractional', 'full_time'], label: 'Work I take', visibility: 'public', master: 'P17.engagementTypes[]',
        labels: { review: 'Reviews', build: 'Builds', advisory: 'Advisory', fractional: 'Fractional (part-time ongoing)', full_time: 'Full-time' } },
      { key: 'acceptsPaymentIn', type: 'list', values: ['money', 'mox'], label: 'I accept payment in', master: 'P17.acceptsPaymentIn[]', labels: { money: 'Money', mox: 'MOX' } }
    ]
  },
  {
    key: 'workPrefs', block: 'P18', label: 'Work preferences', audience: 'reviewer', editor: 'self',
    blurb: 'What you want to be offered, and who must never see you.',
    fields: [
      { key: 'industries', type: 'list', max: 20, label: 'Industries', visibility: 'members', master: 'P18.industries[]' },
      { key: 'openToAudience', type: 'enum', values: ['nobody', 'staff_only', 'members', 'public'], label: 'Show that I am open to work to', master: 'P18.openTo',
        labels: { nobody: 'Nobody', staff_only: 'Moksy staff only', members: 'Moksy members', public: 'Everyone' } },
      { key: 'openToSince', type: 'date', label: 'Open since', master: 'P18.openTo' },
      { key: 'hideFrom', type: 'list', max: 20, label: 'Never show me to (companies)', master: 'P18.hideFrom[]' }
    ]
  },
  {
    key: 'testimonials', block: 'P21', label: 'Testimonials', audience: 'reviewer', editor: 'self',
    blurb: 'From people outside Moksy. Always labelled external, and never counted towards your level.',
    fields: [
      { key: 'items', type: 'objects', max: 10, label: 'Testimonials', visibility: 'public', master: 'P21.externalTestimonials[]', fields: [
        { key: 'name', type: 'string', max: 120, label: 'Name', required: true },
        { key: 'company', type: 'string', max: 120, label: 'Company' },
        { key: 'email', type: 'string', max: 200, label: 'Email (for verification, never shown)' },
        { key: 'text', type: 'text', max: 1000, label: 'What they said', required: true },
        { key: 'verified', type: 'bool', label: 'Verified by staff', editor: 'staff' }
      ] }
    ]
  },
  {
    key: 'network', block: 'P23', label: 'Blocked people', audience: 'base', editor: 'system',
    blurb: 'People who cannot message you and are never paired with you. You block someone from their profile or a conversation, not by typing an id here.',
    fields: [
      { key: 'blocked', type: 'list', max: 200, picker: 'person', label: 'Blocked', master: 'P23.edge: blocked' }
    ]
  },
  {
    key: 'tax', block: 'P28', label: 'Tax form', audience: 'reviewer', editor: 'self', roles: ['reviewer', 'worker', 'owner'],
    blurb: 'Only needed if your payouts touch the US tax system. Most reviewers outside the US choose “No US tax form needed”.',
    fields: [
      { key: 'taxFormKind', type: 'enum', values: ['none', 'w9', 'w8ben', 'w8bene'], label: 'Which applies to you', master: 'P28.taxForm',
        labels: { none: 'No US tax form needed', w9: 'US person — form W-9', w8ben: 'Individual outside the US — form W-8BEN', w8bene: 'Company outside the US — form W-8BEN-E' } },
      { key: 'taxFormSignedAt', type: 'date', label: 'Signed on', master: 'P28.taxForm' }
    ]
  },
  {
    key: 'comms', block: 'P29', label: 'Message timing', audience: 'base', editor: 'self',
    blurb: 'How often we write to you, and the hours we stay quiet.',
    fields: [
      { key: 'digest', type: 'enum', values: ['instant', 'daily', 'weekly'], label: 'Send updates', master: 'P29.digest', labels: { instant: 'As they happen', daily: 'Once a day', weekly: 'Once a week' } },
      { key: 'quietFrom', type: 'string', max: 5, pattern: '^([01][0-9]|2[0-3]):[0-5][0-9]$', label: 'Quiet from', placeholder: '22:00', master: 'P29.quietHours' },
      { key: 'quietTo', type: 'string', max: 5, pattern: '^([01][0-9]|2[0-3]):[0-5][0-9]$', label: 'Quiet until', placeholder: '07:00', master: 'P29.quietHours' }
    ]
  },
  {
    key: 'privacy', block: 'P30', label: 'What others see', audience: 'base', editor: 'self',
    blurb: 'Choose who sees each part of your public profile. Contact details and anything used for payouts are never public.',
    fields: [
      { key: 'showEarnings', type: 'bool', label: 'Show my earnings on my profile', roles: ['reviewer', 'worker'], master: 'P30.showEarnings, showLevel' },
      { key: 'showLevel', type: 'bool', label: 'Show my level on my profile', roles: ['reviewer', 'worker', 'applicant'], master: 'P30.showEarnings, showLevel' },
      { key: 'blockVisibility', type: 'objects', max: 20, label: 'Hide parts of my public profile', addLabel: 'Hide a part of my profile', master: 'P30.blockVisibility', fields: [
        { key: 'section', type: 'enum', values: PUBLIC_SECTIONS, labels: PUBLIC_SECTION_LABELS, label: 'Part', required: true },
        { key: 'visibility', type: 'enum', values: VISIBILITY_LEVELS, label: 'Visible to', required: true,
          labels: { public: 'Everyone', members: 'Moksy members', counterparties: 'People I work with', staff: 'Moksy staff', self: 'Only me' } }
      ] }
    ]
  },
  {
    key: 'consent', block: 'P31', label: 'Age and agreements', audience: 'base', editor: 'self',
    blurb: 'Moksy is for people aged 18 and over, and this is what you have agreed to.',
    fields: [
      { key: 'ageConfirmed', type: 'bool', label: 'I confirm I am 18 or over', master: 'P31.ageConfirmed' },
      { key: 'ageConfirmedAt', type: 'date', label: 'Confirmed on', stamped: true, master: 'P31.ageConfirmed' },
      // GC-018: P31 declared `agreements[]` and recorded its own status as
      // "terms not stored" — so sign-up printed "you agree to our Terms" and
      // kept no record of WHICH terms. Written by the server at sign-up (and
      // at any later acceptance), never by the person agreeing: a consent
      // record its own subject can edit is not a record, which is why this
      // is editor 'system' and shown read-only.
      { key: 'agreements', type: 'objects', max: 24, label: 'What you have agreed to', editor: 'system', master: 'P31.agreements[]', fields: [
        { key: 'kind', type: 'enum', values: AGREEMENT_KINDS, label: 'Agreement', required: true,
          labels: { terms: 'Terms of Service', privacy: 'Privacy Policy', reviewer_agreement: 'Reviewer agreement', code_of_conduct: 'Code of conduct', card_mandate: 'Card mandate', ownership_declaration: 'Ownership declaration' } },
        { key: 'version', type: 'string', max: 40, label: 'Version', required: true },
        { key: 'acceptedAt', type: 'date', label: 'Accepted on', stamped: true },
        { key: 'surface', type: 'string', max: 40, label: 'Where' }
      ] }
    ]
  },
  {
    key: 'aiContext', block: 'P33', label: 'Moksy AI context', audience: 'base', editor: 'self',
    blurb: 'What Moksy AI knows about why you are here, so it never asks twice.',
    fields: [
      { key: 'subject', type: 'enum', values: CAPACITY.subject, label: 'I am', master: 'P33.capacity', labels: { individual: 'An individual', legal_entity: 'A company or organisation' } },
      { key: 'behalf', type: 'enum', values: CAPACITY.behalf, label: 'On behalf of', master: 'P33.capacity', labels: { nobody: 'Myself', education: 'Education', small_business: 'A small business', large_corporation: 'A large corporation', government: 'Government', agency: 'An agency', company: 'My company', startup: 'A startup' } },
      { key: 'intent', type: 'enum', values: CAPACITY.intent, label: 'I am here to', master: 'P33.capacity', labels: { build: 'Plan something to build', review: 'Review others’ plans', spend: 'Buy expert help', earn: 'Earn', invest: 'Invest', join_staff: 'Work for Moksy', partner: 'Partner with Moksy' } },
      { key: 'standing', type: 'enum', values: CAPACITY.standing, label: 'Best described as', master: 'P33.capacity' },
      { key: 'detail', type: 'enum', values: ['brief', 'standard', 'deep'], label: 'How much detail', master: 'P33.workingStyle', labels: { brief: 'Brief — just the answer', standard: 'Standard', deep: 'Deep — show me the reasoning' } },
      { key: 'tone', type: 'enum', values: ['plain', 'friendly', 'formal'], label: 'Tone', master: 'P33.workingStyle' },
      { key: 'preferredFormat', type: 'enum', values: ['prose', 'bullets', 'tables'], label: 'Format', master: 'P33.workingStyle', labels: { prose: 'Paragraphs', bullets: 'Bullet points', tables: 'Tables' } },
      { key: 'goals', type: 'objects', max: 10, label: 'Goals', master: 'P33.goals[]', fields: [
        { key: 'text', type: 'string', max: 280, label: 'Goal', required: true },
        { key: 'horizon', type: 'enum', values: ['weeks', 'months', 'year', 'years'], label: 'By when', labels: { weeks: 'Within weeks', months: 'Within months', year: 'Within a year', years: 'Longer term' } },
        { key: 'projectRef', type: 'string', max: 64, label: 'Linked project (optional)' }
      ] },
      // Written by Moksy AI from what the person says, never typed in: a
      // "fact" somebody types about themselves with origin "inferred" is a
      // contradiction. Shown so they can see what Moksy AI holds.
      { key: 'holderFacts', type: 'objects', max: 24, label: 'What Moksy AI remembers about you', editor: 'system', master: 'P33.holderFacts[]', fields: [
        { key: 'fact', type: 'string', max: 80, label: 'Fact', required: true },
        { key: 'value', type: 'string', max: 280, label: 'Value', required: true },
        { key: 'origin', type: 'enum', values: ['said', 'inferred', 'imported'], label: 'Where from', labels: { said: 'You told us', inferred: 'Worked out from your answers', imported: 'Imported' } },
        { key: 'observedAt', type: 'date', label: 'Observed' }
      ] }
    ]
  }
]);

/**
 * Fields added to records that already exist as arrays on User. The CRUD
 * routes for these already exist; the extension widens them rather than
 * adding a second place to write.
 */
export const RECORD_EXTENSIONS = Object.freeze({
  skills: [
    { key: 'level', type: 'int', min: 0, max: 5, label: 'How good', master: 'P06.skills[].level',
      labels: { 0: 'Not rated', 1: 'Aware of it', 2: 'Novice', 3: 'Intermediate', 4: 'Advanced', 5: 'Expert' } },
    { key: 'anchoredTo', type: 'list', max: 10, picker: 'evidence', label: 'Proven by', hint: 'The job, qualification or certificate where you used it. A skill counts for matching only once it is proven.', master: 'P06.skills[].anchoredTo[]' }
  ],
  education: [
    { key: 'eqfLevel', type: 'int', min: 1, max: 8, label: 'Level', master: 'P07.education[].eqfLevel',
      labels: { 1: 'Basic education', 2: 'Lower secondary', 3: 'Upper secondary', 4: 'Vocational / A-level', 5: 'Short-cycle higher', 6: "Bachelor's", 7: "Master's", 8: 'Doctorate' } },
    { key: 'skills', type: 'list', max: 12, label: 'Skills', master: 'P07.education[].skills[]' }
  ],
  certifications: [
    { key: 'kind', type: 'enum', values: ['certification', 'licence', 'badge', 'membership'], label: 'Kind', master: 'P08.credentials[].kind', labels: { membership: 'Professional membership' } },
    { key: 'credentialUrl', type: 'string', max: 500, label: 'Verification link', master: 'P08.credentials[].credentialId, credentialUrl' },
    { key: 'validIn', type: 'list', max: 20, picker: 'country', label: 'Valid in', master: 'P08.credentials[].validIn' },
    { key: 'skills', type: 'list', max: 12, label: 'Skills', master: 'P08.credentials[].skills[]' }
  ],
  employment: [
    roleKeyField({ key: 'roleKey', label: 'Closest role', master: 'P09.experience[].roleKey' }),
    rungField({ key: 'rung', master: 'P09.experience[].rung' }),
    { key: 'employmentType', type: 'enum', values: ['employee', 'contractor', 'freelance', 'founder', 'volunteer', 'intern'], label: 'Type', master: 'P09.experience[].employmentType' },
    { key: 'locationType', type: 'enum', values: ['onsite', 'hybrid', 'remote'], label: 'Where', master: 'P09.experience[].locationType', labels: { onsite: 'On site' } },
    { key: 'skills', type: 'list', max: 12, label: 'Skills', master: 'P09.experience[].skills[]' }
  ],
  // An organisation membership says which company; this says in what relation.
  organizations: [
    { key: 'relation', type: 'enum', values: ['owner', 'employee', 'contractor', 'agency_member', 'client_contact', 'reviewer_company', 'partner'], label: 'I am their', master: 'P24.relation',
      labels: { owner: 'Owner', employee: 'Employee', contractor: 'Contractor', agency_member: 'Agency member', client_contact: 'Client contact', reviewer_company: 'Reviewer company member', partner: 'Partner' } },
    { key: 'title', type: 'string', max: 120, label: 'Job title there', master: 'P24.title, roleKey, rung' },
    roleKeyField({ key: 'roleKey', label: 'Closest role', master: 'P24.title, roleKey, rung' }),
    rungField({ key: 'rung', master: 'P24.title, roleKey, rung' })
  ]
});

/**
 * 🧾 The CORE fields of each career record — what the record IS, as opposed
 * to the refinements in RECORD_EXTENSIONS above.
 *
 * These used to be typed three times: the Mongoose model, the users-service
 * contract and the dashboard's copy of it. The model and the contracts all
 * held startDate / endDate / credentialId, and the one surface a person
 * actually uses — the /profile forms — was a fourth, hand-written copy that
 * asked for Company, Title and Description only. So twenty years of
 * experience could not be stated, a certificate's expiry could not be
 * entered, and nothing could be corrected without deleting the record
 * (GC-032, GC-033, GC-034, GC-035).
 *
 * Declared once here. The model, both contracts and BOTH the add form and the
 * edit-in-place form are generated from it, so a field declared here is a
 * field a person can fill in, and there is no form to forget.
 *
 *   month        a 'YYYY-MM' string (older records may hold a full date,
 *                which still reads and still sorts)
 *   vocabulary   the controlled vocabulary the field suggests from, and
 *                captures new words into for approval
 *   modelRequired  the model refuses a record without it (skills[].name only;
 *                  older rows of the other arrays predate `required`)
 *   minLength    the shortest real answer
 *   clears       a bool that, when on, makes these fields meaningless
 *                ("I work here now" → no end date); the form clears them
 *
 * `atEntry` on an EXTENSION field puts it on the add form too — a
 * certificate's verification link is known the moment it is typed in.
 */
export const RECORD_CORE = Object.freeze({
  skills: [
    { key: 'name', type: 'string', max: 80, label: 'Skill', required: true, modelRequired: true, vocabulary: 'skill', placeholder: 'Start typing — e.g. React' }
  ],
  education: [
    { key: 'school', type: 'string', max: 200, minLength: 2, label: 'School', required: true, placeholder: 'Where you studied' },
    { key: 'degree', type: 'string', max: 120, minLength: 2, label: 'Degree', required: true, vocabulary: 'degree', placeholder: 'e.g. BSc' },
    { key: 'field', type: 'string', max: 120, label: 'Field', vocabulary: 'field_of_study', placeholder: 'e.g. Computer Science' },
    { key: 'startDate', type: 'month', label: 'Started' },
    { key: 'endDate', type: 'month', label: 'Finished', hint: 'Or when you expect to finish.' }
  ],
  employment: [
    { key: 'company', type: 'string', max: 200, minLength: 2, label: 'Company', required: true, vocabulary: 'company', placeholder: 'Where you worked' },
    { key: 'title', type: 'string', max: 200, minLength: 2, label: 'Title', required: true, vocabulary: 'profession', placeholder: 'e.g. Senior Software Engineer' },
    { key: 'startDate', type: 'month', label: 'Started' },
    { key: 'endDate', type: 'month', label: 'Ended', hint: 'Leave blank if you work here now.' },
    { key: 'current', type: 'bool', label: 'I work here now', clears: ['endDate'] },
    { key: 'description', type: 'text', max: 1000, label: 'Description' }
  ],
  certifications: [
    { key: 'name', type: 'string', max: 200, minLength: 2, label: 'Certification', required: true, vocabulary: 'certification', placeholder: 'e.g. CISSP' },
    { key: 'issuer', type: 'string', max: 200, minLength: 2, label: 'Issuer', required: true, vocabulary: 'issuer', placeholder: 'e.g. ISC²' },
    { key: 'credentialId', type: 'string', max: 120, label: 'Credential ID' },
    { key: 'issueDate', type: 'month', label: 'Issued' },
    { key: 'expiresDate', type: 'month', label: 'Expires', hint: 'Leave blank if it does not expire.' }
  ]
});

// Extensions a person knows at entry, so the add form asks for them too.
const AT_ENTRY = Object.freeze({ certifications: ['credentialUrl'] });

/** 'YYYY', 'YYYY-MM', or an older full date / ISO string. */
export const MONTH_PATTERN = '^\\d{4}(-(0[1-9]|1[0-2]))?(-\\d{2}.*)?$';

/** The fields the ADD form asks for: the core, plus extensions flagged at entry. */
export const entryFields = (record) => [
  ...(RECORD_CORE[record] || []),
  ...(RECORD_EXTENSIONS[record] || []).filter((f) => (AT_ENTRY[record] || []).includes(f.key))
];

/** An empty draft for a record's add form. */
export const emptyRecord = (record, fields = entryFields(record)) => Object.fromEntries(fields.map((f) => [
  f.key, f.type === 'list' ? [] : f.type === 'bool' ? false : ''
]));

/**
 * What is wrong with a whole career record, beyond each field's own type —
 * the rules that need two fields at once. Pure, so the service checks the
 * MERGED record (an edit that only moves the end date must still be after the
 * start) and the form can say the same thing before it sends.
 */
export const careerRecordProblems = (record, entry = {}) => {
  const problems = [];
  const present = (v) => v != null && String(v).trim() !== '';
  const span = { education: ['startDate', 'endDate'], employment: ['startDate', 'endDate'], certifications: ['issueDate', 'expiresDate'] }[record];
  if (span) {
    const [from, to] = span.map((k) => entry?.[k]);
    if (present(from) && present(to) && String(to).slice(0, 7) < String(from).slice(0, 7)) {
      const label = (k) => (RECORD_CORE[record].find((f) => f.key === k)?.label || k).toLowerCase();
      problems.push(`The ${label(span[1])} date is before the ${label(span[0])} date.`);
    }
  }
  if (record === 'employment' && entry?.current === true && present(entry?.endDate)) {
    problems.push('A job you still hold has no end date — clear "Ended" or untick "I work here now".');
  }
  return problems;
};

/** P01, held on the Person collection rather than on the account. */
export const PERSON_CORE = Object.freeze({
  collection: 'people',
  states: ['lead', 'invited', 'guest', 'member', 'former_member', 'suspended', 'erased', 'merged', 'unknown'],
  origins: ['signup', 'guest', 'invite', 'enquiry', 'job_source', 'team_plan', 'referral', 'import', 'seed'],
  master: {
    'P01.personId': 'Person.personId',
    'P01.state': 'Person.state',
    'P01.origin': 'Person.origin',
    'P01.originRef': 'Person.originRef',
    'P01.mergedInto': 'Person.mergedInto',
    'P01.accountId': 'Person.accountId',
    'P01.isTest': 'Person.isTest'
  }
});

/**
 * Master fields stored on the account outside `person`, whose storage this
 * build fixed rather than added. `about.avatarIcon` was accepted by the
 * contract and read by three screens while the strict model dropped it on
 * every save; it is declared on the model now.
 */
export const ACCOUNT_FIELDS = Object.freeze({
  'P02.avatarIcon': 'User.about.avatarIcon'
});

// ── generated views ──────────────────────────────────────────────────────
export const sectionByKey = (key) => PERSON_SECTIONS.find((s) => s.key === key) || null;

/** May this editor change this field? Staff may change everything. */
export const editableBy = (section, field, as) =>
  as === 'staff' || ((field.editor || section.editor) === 'self' && !field.stamped);

/**
 * Every master-schema field that is now stored, and where. Built by walking
 * the declarations above, so declaring a field is what makes the Schemas
 * screen call it stored — there is no second list to forget.
 */
export const STORED_MASTER_FIELDS = (() => {
  const out = {};
  const add = (master, path) => { for (const m of [].concat(master || [])) (out[m] = out[m] || []).push(path); };
  for (const s of PERSON_SECTIONS) {
    for (const f of s.fields) {
      add(f.master, `User.person.${s.key}.${f.key}`);
      for (const sub of f.fields || []) add(sub.master, `User.person.${s.key}.${f.key}[].${sub.key}`);
    }
  }
  const arrayName = { skills: 'skills', education: 'education', certifications: 'certifications', employment: 'employment', organizations: 'organizations' };
  for (const [rec, fields] of Object.entries(RECORD_EXTENSIONS)) {
    for (const f of fields) add(f.master, `User.${arrayName[rec]}[].${f.key}`);
  }
  for (const [m, p] of Object.entries(PERSON_CORE.master)) add(m, p);
  for (const [m, p] of Object.entries(ACCOUNT_FIELDS)) add(m, p);
  return Object.freeze(out);
})();

const MONGO_TYPE = { string: String, text: String, enum: String, bool: Boolean, int: Number, number: Number, date: Date };

const mongoField = (f) => {
  if (f.type === 'list') return { type: [String], default: undefined };
  if (f.type === 'objects') return { type: [Object.fromEntries(f.fields.map((sub) => [sub.key, mongoField(sub)]))], default: undefined };
  const def = { type: MONGO_TYPE[f.type] || String };
  if (f.type === 'enum') def.enum = [...f.values, null, ''];
  return def;
};

/**
 * The Mongoose definition of `User.person`. Plain constructors only, so this
 * file needs no mongoose import. Arrays default to `undefined` so an untouched
 * person does not grow thirty empty arrays on every save.
 */
export const personMongooseShape = () => Object.fromEntries(PERSON_SECTIONS.map((s) => [
  s.key, Object.fromEntries(s.fields.map((f) => [f.key, mongoField(f)]))
]));

/**
 * The Mongoose fields of a career record's core. `modelRequired` only — a
 * `required` on older arrays would refuse to save every legacy row missing it.
 */
export const coreMongooseShape = (record) =>
  Object.fromEntries((RECORD_CORE[record] || []).map((f) => [
    f.key, f.modelRequired ? { ...mongoField(f), required: true } : mongoField(f)
  ]));

/** The Mongoose fields to merge into an existing array's sub-record. */
export const extensionMongooseShape = (record) =>
  Object.fromEntries((RECORD_EXTENSIONS[record] || []).map((f) => [f.key, mongoField(f)]));

/**
 * What of `person` may leave the service on a public profile: only fields
 * declared `public`, and not a block the person has tightened.
 */
export const publicPerson = (person = {}) => {
  const tightened = new Set((person?.privacy?.blockVisibility || [])
    .filter((b) => b && b.visibility && b.visibility !== 'public').map((b) => b.section));
  const out = {};
  for (const s of PERSON_SECTIONS) {
    if (tightened.has(s.key)) continue;
    for (const f of s.fields) {
      if (f.visibility !== 'public') continue;
      const v = person?.[s.key]?.[f.key];
      if (v == null || (Array.isArray(v) && !v.length) || v === '') continue;
      let value = v;
      if (f.type === 'objects') {
        // A staff-only sub-field (e.g. testimonial `verified`) is shown; an
        // email captured for verification is not public and never leaves.
        value = v.map((item) => Object.fromEntries(f.fields
          .filter((sub) => sub.key !== 'email')
          .map((sub) => [sub.key, item?.[sub.key]])
          .filter(([, x]) => x != null && x !== '')));
      }
      (out[s.key] = out[s.key] || {})[f.key] = value;
    }
  }
  return out;
};

/**
 * The WHOLE public profile of an account — what a stranger sees at
 * /people/:handle — not just the `person` blocks.
 *
 * Declared here because it was declared nowhere: the users service built this
 * shape inline, so the only way for the account holder to learn what their
 * public profile contains was to publish it and look. They could not preview
 * it beforehand (GC-081), and a preview built beside the real thing would
 * drift from it the first time a field moved.
 *
 * One function, two callers: the users service projects a stored user through
 * it, and the dashboard projects the owner's own record through it to draw the
 * preview. A field not listed here reaches neither.
 *
 * `about.title` is the HONORIFIC and is passed through as such — the surface
 * shows it as part of the name. The headline is `person.names.headline`.
 */
export const publicProfileOf = (user = {}) => ({
  id: String(user._id || user.id || ''),
  type: 'user',
  username: user.username,
  role: user.role,
  about: {
    title: user.about?.title || '',
    firstName: user.about?.firstName || '',
    lastName: user.about?.lastName || '',
    shortBio: user.about?.shortBio || '',
    photoUrl: user.about?.photoUrl || '',
    // Read by the public page's avatar fallback and never sent, so an account
    // showing a chosen icon fell back to its initials for everybody else.
    avatarIcon: user.about?.avatarIcon || ''
  },
  workStatus: {
    acceptingRequests: !!user.workStatus?.acceptingRequests
  },
  profilePublic: !!user.settings?.privacy?.profilePublic,
  employment: Array.isArray(user.employment) ? user.employment : [],
  education: Array.isArray(user.education) ? user.education : [],
  skills: Array.isArray(user.skills) ? user.skills : [],
  certifications: Array.isArray(user.certifications) ? user.certifications : [],
  // Master-schema blocks, public fields only. The projection is declared
  // beside the fields (`visibility` above), so a new field is private until
  // somebody decides otherwise — never the reverse.
  person: publicPerson(user.person || {}),
  createdAt: user.createdAt,
  updatedAt: user.updatedAt
});
